import { useEffect, type Dispatch, type SetStateAction } from "react";
import { useLocation } from "react-router";

const MOBILE_BREAKPOINT_PX = 576;

/** Deixa o React Router iniciar a navegação antes de fechar o drawer (evita competir com o render). */
function deferCloseSidebar(
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>
) {
  window.setTimeout(() => {
    setIsSidebarOpen(false);
  }, 0);
}

/**
 * No mobile, fecha a sidebar ao escolher um item do menu.
 * - Links internos (`/…`): fecha após a troca de rota (conteúdo alinha melhor com o fechamento).
 * - `href` que não é rota SPA (`#`, empresa, etc.): fecha no próximo tick.
 * - Botão Voltar (super admin): idem.
 * Fase bubble no `click` + fechamento na rota reduz “travão” (não é rede).
 */
export function useMobileSidebarCloseOnMenuClick(
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>
) {
  const location = useLocation();

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`);
    if (!mq.matches) return;
    setIsSidebarOpen(false);
  }, [location.pathname, setIsSidebarOpen]);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`);

    const handleClick = (e: MouseEvent) => {
      if (!mq.matches) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const aside = target.closest(".zing-inner-page aside:first-of-type");
      if (!aside) return;

      const link = target.closest("nav a") as HTMLAnchorElement | null;
      if (link) {
        const href = (link.getAttribute("href") || "").trim();
        const isInternalSpaPath =
          href.startsWith("/") ||
          href.startsWith("http://") ||
          href.startsWith("https://");
        if (isInternalSpaPath) {
          return;
        }
        deferCloseSidebar(setIsSidebarOpen);
        return;
      }

      const voltar = target.closest('[role="button"][aria-label="Voltar"]');
      if (voltar && aside.contains(voltar)) {
        deferCloseSidebar(setIsSidebarOpen);
      }
    };

    document.addEventListener("click", handleClick, false);
    return () => document.removeEventListener("click", handleClick, false);
  }, [setIsSidebarOpen]);
}
